import { Alert, Box, Button, FormControl, FormControlLabel, Grid, Radio, RadioGroup, Typography } from '@mui/material';

import {
	header,
	car,
	driver,
	buttonName,
	offsegGetType,
	organization,
	g500LiteGetType,
	coexistenceGetType,
	inputDateErrorAlert,
} from '../../../common/const/commonConst';
import { alert, searchButton } from '../../../common/const/componentSize';
import { DialogType } from '../../../common/type/DialogType';
import { SearchAutocomplete, SearchStyledAutocomplete } from '../../../common/component/parts/SearchAutocomplete';
import { SearchDate } from '../../../common/component/parts/SearchDate';
import { useEffectMasterInfo } from '../../../common/component/hooks/useEffectMasterInfo';
import { useStateSelectedOffsegMasterInfo } from '../../../common/component/hooks/useStateSelectedOffsegMasterInfo';
import { useStateSelectedG500LiteMasterInfo } from '../../../common/component/hooks/useStateSelectedG500LiteMasterInfo';
import { useEffectOffsegMasterInfo } from '../../../common/component/hooks/useEffectOffsegMasterInfo';
import { useEffectG500LiteMasterInfo } from '../../../common/component/hooks/useEffectG500LiteMasterInfo';
import { useStateSearchKey } from '../../../common/component/hooks/useStateSearchKey';
import { useStateSearchDate } from '../../../common/component/hooks/useStateSearchDate';

import { useGetCarUseResult } from '../hooks/useGetCarUseResult';
import { search } from '../../const/carUseResultPageSize';
import { CarUseResultInfoType } from '../../feature/type/CarUseResultInfoType';

import theme from '../../../theme';

type PropsType = {
	setCarUseResultInfoList: React.Dispatch<React.SetStateAction<CarUseResultInfoType[]>>;
	setIsAsyncProcess: React.Dispatch<React.SetStateAction<boolean>>;
	setDialog: React.Dispatch<React.SetStateAction<DialogType>>;
};

/**
 * 車両利用実績一覧検索
 */
export const CarUseResultSearch = (props: PropsType) => {
	const { setCarUseResultInfoList, setIsAsyncProcess, setDialog } = props;

	const { masterInfo, setMasterInfo } = useEffectMasterInfo(coexistenceGetType.carUseResult, setDialog); // マスタデータの状態管理用カスタムフック
	const { selectedOffsegMasterInfo, setSelectedOffsegMasterInfo, onChangeOffsegOrganization, onChangeOffsegCar, onChangeOffsegDriver } =
		useStateSelectedOffsegMasterInfo(); // Offsegの選択したマスタデータの状態管理用カスタムフック
	const { selectedG500LiteMasterInfo, setSelectedG500LiteMasterInfo, onChangeG500LiteOrganization, onChangeG500LiteCar } =
		useStateSelectedG500LiteMasterInfo(); // G500Liteの選択したマスタデータの状態管理用カスタムフック
	const { isCarSelectedAsKey, onChangeOffsegSearchKey } = useStateSearchKey({ selectedOffsegMasterInfo, setSelectedOffsegMasterInfo }); // Offsegの検索キー（車両 or ドライバー）の状態管理用カスタムフック
	const { searchDate, isInputDateError, isAlertOpened, handleChangeSearchDate, onCloseInputDateErrorAlert } = useStateSearchDate(); // 検索日の状態管理用カスタムフック

	const useEffectOffsegMasterInfoInput = {
		getType: offsegGetType.carAndDriverInfo,
		selectedOffsegMasterInfo,
		setSelectedOffsegMasterInfo,
		masterInfo,
		setMasterInfo,
		setDialog,
	};
	useEffectOffsegMasterInfo(useEffectOffsegMasterInfoInput); // Offsegマスタデータの状態管理用カスタムフック

	const useEffectG500LiteMasterInfoInput = {
		getType: g500LiteGetType.carInfo,
		selectedG500LiteMasterInfo,
		setSelectedG500LiteMasterInfo,
		masterInfo,
		setMasterInfo,
		setDialog,
	};
	useEffectG500LiteMasterInfo(useEffectG500LiteMasterInfoInput); // G500Liteマスタデータの状態管理用カスタムフック

	const useGetCarUseResultInput = {
		setIsAsyncProcess,
		isCarSelectedAsKey,
		selectedOffsegMasterInfo,
		selectedG500LiteMasterInfo,
		searchDate,
		masterInfo,
		setDialog,
		setCarUseResultInfoList,
	};
	const { onClickSearch } = useGetCarUseResult(useGetCarUseResultInput); // 車両利用実績一覧取得用カスタムフック

	return (
		<Box sx={{ height: search.height }} display="flex">
			<Grid container direction="column" justifyContent="flex-start" alignItems="center" spacing={1.5}>
				<Grid item>
					<Grid container direction="row" justifyContent="space-between" alignItems="center">
						<Grid item xs={6}>
							<Typography
								role="lable"
								aria-label={header.offseg}
								position="relative"
								sx={{ fontSize: 17, color: theme.palette.primary.dark, width: search.halfWidth }}
							>
								{header.offseg}
							</Typography>
						</Grid>
						<Grid item xs={6}>
							{/* 車両/ドライバーの選択ボタン */}
							<FormControl sx={{ width: search.halfWidth, alignItems: 'flex-end' }}>
								<RadioGroup row defaultValue={car.id} onChange={(e, value) => onChangeOffsegSearchKey(e, value)}>
									<FormControlLabel
										value={car.id}
										control={<Radio id="carRadio" size="small" />}
										label={<Typography sx={{ fontSize: 15, color: theme.palette.common.black }}>{car.label}</Typography>}
									/>
									<FormControlLabel
										value={driver.id}
										control={<Radio id="driverRadio" size="small" />}
										label={<Typography sx={{ fontSize: 15, color: theme.palette.common.black }}>{driver.label}</Typography>}
									/>
								</RadioGroup>
							</FormControl>
						</Grid>
					</Grid>
				</Grid>
				<Grid item>
					{/* Offseg組織 */}
					<FormControl>
						<SearchAutocomplete
							id="offsegOrganization"
							options={masterInfo.offsegOrganizationInfoList.map((item) => ({
								id: item.organizationId,
								label: item.organizationName,
							}))}
							label={organization.label}
							value={selectedOffsegMasterInfo.selectedOffsegOrganizationInfo}
							onChange={(e, value) => onChangeOffsegOrganization(e, value)}
						/>
					</FormControl>
				</Grid>
				<Grid item>
					{/* Offseg車両 */}
					<FormControl>
						{isCarSelectedAsKey && (
							<SearchAutocomplete
								id="offsegCar"
								options={masterInfo.offsegCarInfoList.map((item) => ({ id: item.carId, label: item.carCode }))}
								label={car.label}
								value={selectedOffsegMasterInfo.selectedOffsegCarInfo}
								onChange={(e, value) => onChangeOffsegCar(e, value)}
							/>
						)}
						{!isCarSelectedAsKey && <SearchStyledAutocomplete label={car.label} />}
					</FormControl>
				</Grid>
				<Grid item>
					{/* Offsegドライバー */}
					<FormControl>
						{!isCarSelectedAsKey && (
							<SearchAutocomplete
								id="offsegDriver"
								options={masterInfo.offsegDriverInfoList.map((item) => ({ id: item.driverId, label: item.driverName }))}
								label={driver.label}
								value={selectedOffsegMasterInfo.selectedOffsegDriverInfo}
								onChange={(e, value) => onChangeOffsegDriver(e, value)}
							/>
						)}
						{isCarSelectedAsKey && <SearchStyledAutocomplete label={driver.label} />}
					</FormControl>
				</Grid>
				<Grid item>
					<Typography
						role="lable"
						aria-label={header.g500Lite}
						position="relative"
						sx={{ fontSize: 17, color: theme.palette.primary.dark, width: search.width }}
					>
						{header.g500Lite}
					</Typography>
				</Grid>
				<Grid item>
					{/* G500Lite組織 */}
					<FormControl>
						<SearchAutocomplete
							id="g500LiteOrganization"
							options={masterInfo.g500LiteOrganizationInfoList.map((item) => ({
								id: item.organizationId,
								label: item.organizationName,
							}))}
							label={organization.label}
							value={selectedG500LiteMasterInfo.selectedG500LiteOrganizationInfo}
							onChange={(e, value) => onChangeG500LiteOrganization(e, value)}
						/>
					</FormControl>
				</Grid>
				<Grid item>
					{/* G500Lite車両 */}
					<FormControl>
						<SearchAutocomplete
							id="g500LiteCar"
							options={masterInfo.g500LiteCarInfoList.map((item) => ({ id: item.carId, label: item.carCode }))}
							label={car.label}
							value={selectedG500LiteMasterInfo.selectedG500LiteCarInfo}
							onChange={(e, value) => onChangeG500LiteCar(e, value)}
						/>
					</FormControl>
				</Grid>
				<Grid item>
					<Box sx={{ width: search.width, display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
						<Typography role="lable" aria-label={header.searchDate} sx={{ fontSize: 17, color: theme.palette.primary.dark }}>
							{header.searchDate}
						</Typography>
					</Box>
				</Grid>
				<Grid item alignSelf={'start'}>
					{/* 検索日 */}
					<SearchDate
						searchDate={searchDate}
						handleChangeSearchDate={handleChangeSearchDate}
					/>
				</Grid>
				<Grid item>
					<Grid container direction="row" justifyContent="space-between" alignItems="flex-start" spacing={3}>
						<Grid item>
							{/* アラート表示 */}
							<Box sx={{ width: alert.width, position: 'relative' ,left: '8px'}}>
								{isAlertOpened && (
									<Alert severity="error" onClose={onCloseInputDateErrorAlert}>
										<Typography sx={{ fontSize: 13.5, color: theme.palette.common.black }}>{inputDateErrorAlert.message}</Typography>
									</Alert>
								)}
							</Box>
						</Grid>
						<Grid item>
							{/* 検索ボタン */}
							<Box position="relative" right="6px">
								<Button id="searchButton" variant="contained" onClick={onClickSearch} disabled={isInputDateError} sx={{ width: searchButton.width }}>
									<Typography sx={{ fontSize: 15, color: theme.palette.common.white }}>{buttonName.search}</Typography>
								</Button>
							</Box>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Box>
	);
};
